<script>
import { mapActions, mapState } from 'vuex';
import { useAlert } from 'dashboard/composables';
import LoadingState from '../../../../components/widgets/LoadingState.vue';
import EditColDescModal from './EditColDescModal.vue';
import EditAllowedValuesModal from './EditAllowedValuesModal.vue';

export default {
  name: "GSheetConfig",
  components: {
    LoadingState,
    EditColDescModal,
    EditAllowedValuesModal,
  },
  data() {
    return {
      isLoading: false,
      isError: false,
      query: '',
      editObj: {},
      editKey: null,
      isOpenEditModal: false,
      isOpenAllowedValuesModal: false,

      // pagination
      currentPage: 1,
      pageSize: 10,

      // track which rows are being toggled (by id or fallback to column name)
      togglingRows: new Set(),
    };
  },
  computed: {
    ...mapState('gsheet', ['columns']),
    columnList() {
      return this.columns?.columns || {};
    },
    filteredList() {
      const query = this.query.toLowerCase();

      return Object.fromEntries(
        Object.entries(this.columnList).filter(([key, c]) => {
          return (
            key.toLowerCase().includes(query) ||
            (c.description || '').toLowerCase().includes(query)
          );
        })
      );
    },
    paginatedList() {
      const entries = Object.entries(this.filteredList);
      const start = (this.currentPage - 1) * this.pageSize;
      const end = start + this.pageSize;
      return Object.fromEntries(entries.slice(start, end));
    },
    totalPages() {
      return Math.max(
        1,
        Math.ceil(Object.keys(this.filteredList).length / this.pageSize)
      );
    },
  },
  methods: {
    ...mapActions('gsheet', ['fetchColumns', 'editColumnIsEnabled']),
    refreshData() {
      this.$emit('refresh')
    },
    clearQuery() {
      this.query = '';
    },
    handleEdit(key, obj) {
      this.editKey = key;
      this.editObj = obj;
      this.isOpenEditModal = true;
    },
    handleCloseEdit() {
      this.editKey = null;
      this.editObj = null;
      this.isOpenEditModal = false;
    },
    changePage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
      }
    },
    handlePageSizeChange(event) {
      this.pageSize = parseInt(event.target.value, 10);
      this.currentPage = 1;
    },
    handleAllowedValues(key, obj) {
      this.editKey = key;
      this.editObj = obj;
      this.isOpenAllowedValuesModal = true;
    },
    handleCloseAllowedValues() {
      this.editKey = null;
      this.editObj = null;
      this.isOpenAllowedValuesModal = false;
    },

    // Completed toggle handler
    async handleToggleRow(column) {
      const alert = useAlert;
      // use the row id if available, otherwise fall back to column name
      const rowKey = column?.id ?? column?.name;

      if (!rowKey) {
        console.warn('handleToggleRow: missing column id/name', column);
        return;
      }

      // prevent double toggles
      if (this.togglingRows.has(rowKey)) return;

      this.togglingRows.add(rowKey);

      const newState = !Boolean(column.is_enabled);

      const payload = {
        knowledgeId: this.$route.params.GSheetKBId,
        columnName: column.name,
        is_enabled: newState,
      };

      try {
        await this.editColumnIsEnabled(payload);

        this.$emit('refresh');
      } catch (err) {
        console.error('Error toggling column enabled state', err);
        try {
          if (typeof alert === 'function') {
            useAlert('Failed to update column state');
          } else if (typeof alert === 'object' && typeof alert.show === 'function') {
            alert.show('Failed to update column state');
          }
        } catch (e) {
          console.warn('Unable to show alert with useAlert', e);
        }
      } finally {
        this.togglingRows.delete(rowKey);
      }
    },
  },
  watch: {
    query() {
      this.currentPage = 1;
    },
  },
};
</script>

<template>
  <div>
    <!-- Modals -->
    <EditColDescModal
      v-if="isOpenEditModal"
      :edit-key="editKey"
      :edit-obj="editObj"
      @close="handleCloseEdit"
      @refresh="refreshData"
    />

    <EditAllowedValuesModal
      v-if="isOpenAllowedValuesModal"
      :edit-key="editKey"
      :edit-obj="editObj"
      @close="handleCloseAllowedValues"
      @refresh="refreshData"
    />

    <!-- Loading state -->
    <div
      v-if="isLoading"
      class="bg-slate-100 rounded w-full min-h-[300px] flex flex-col items-center justify-center"
    >
      <LoadingState message="Loading data..." />
    </div>

    <!-- Error State -->
    <div
      v-if="isError && !isLoading"
      class="bg-slate-100 rounded w-full min-h-[300px] flex flex-col gap-4 items-center justify-center"
    >
      <fluent-icon icon="warning" size="48" class="text-red-500" />
      <h1>Error loading data. Please try again later.</h1>
    </div>

    <!-- Empty State -->
    <div
      v-if="
        Object.keys(columnList).length === 0 && !isLoading && !isError
      "
      class="bg-slate-100 rounded w-full min-h-[300px] flex flex-col gap-4 items-center justify-center"
    >
      <fluent-icon icon="document" size="48" class="text-slate-600" />
      <h1>The data is empty.</h1>
    </div>

    <!-- Data -->
    <div v-if="!isLoading && !isError && Object.keys(columnList).length > 0" class="p-4">
      <!-- Search query section -->
      <div class="relative max-w-md mb-4">
        <div
          v-if="!query"
          class="absolute inset-y-0 right-0 pr-3 flex items-center justify-center h-10 pointer-events-none"
        >
          <fluent-icon
            icon="search"
            class="h-5 w-5 text-slate-400 dark:text-slate-500"
            aria-hidden="true"
          />
        </div>
        <button
          type="button"
          v-if="query"
          @click="clearQuery"
          class="absolute inset-y-0 right-0 pr-3 flex items-center justify-center h-10"
        >
          <fluent-icon
            icon="dismiss"
            class="h-5 w-5 text-red-400 hover:text-red-600 dark:text-red-300 dark:hover:text-red-400"
          />
        </button>
        <input
          type="text"
          class="bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg py-2 pl-10 pr-10 w-full focus:outline-none focus:ring-2 focus:ring-woot-500 focus:border-transparent transition-all text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500"
          placeholder="Search..."
          v-model="query"
        />
      </div>

      <!-- Table rows -->
      <div
        v-for="(column, key, index) in paginatedList"
        :key="key"
        class="group grid grid-cols-[auto,1fr,2fr,auto] gap-4 px-4 py-3 border-b border-slate-200 dark:border-slate-700 transition-colors duration-200 ease-in-out"
        :class="[
          index % 2 === 0 
            ? 'bg-white dark:bg-slate-800' 
            : 'bg-slate-50/50 dark:bg-slate-700/50',
          'hover:bg-slate-50 dark:hover:bg-slate-700'
        ]"
        :title="column.updated_at ? `Last updated: ${formatDate(column.updated_at)}` : ''"
      >
        <!-- Toggle -->
        <div
          class="font-medium text-slate-700 dark:text-slate-200 whitespace-nowrap row-span-2 flex items-center"
        >
          <template>
            <label
              class="inline-flex items-center"
              :class="{ 
                'cursor-pointer': !togglingRows.has(column.id), 
                'cursor-not-allowed': togglingRows.has(column.id) 
              }"
              :title="togglingRows.has(column.id) 
                ? 'Updating...' 
                : (column.is_enabled ? 'Click to disable' : 'Click to enable')"
            >
              <input
                type="checkbox"
                class="sr-only peer"
                :checked="column.is_enabled"
                @change="handleToggleRow(column)"
                :disabled="togglingRows.has(column.id)"
              />
              <div
                class="w-10 h-5 rounded-full relative transition-all duration-300 ease-in-out"
                :class="{
                  'bg-green-500': column.is_enabled && !togglingRows.has(column.id),
                  'bg-slate-300 dark:bg-slate-600': !column.is_enabled && !togglingRows.has(column.id),
                  'bg-slate-200 dark:bg-slate-500': togglingRows.has(column.id),
                  'opacity-50': togglingRows.has(column.id)
                }"
              >
                <div
                  class="absolute w-4 h-4 bg-white dark:bg-slate-300 rounded-full shadow-sm transition-all duration-300 ease-in-out"
                  :class="{
                    'left-0.5 top-0.5': !column.is_enabled,
                    'left-[1.375rem] top-0.5': column.is_enabled,
                    'animate-pulse': togglingRows.has(column.id)
                  }"
                ></div>
              </div>
            </label>
          </template>
        </div>

        <!-- Name -->
        <div
          class="font-medium text-slate-700 dark:text-slate-200 whitespace-normal break-words row-span-2 flex items-center"
        >
          {{ column.name }}
        </div>

        <!-- Description (row 1) -->
        <div
          :class="[ 
            column.description
              ? 'text-slate-600 dark:text-slate-300'
              : 'text-red-500 dark:text-red-400 italic'
          ]"
          class="whitespace-normal break-words"
        >
          {{ column.description || 'No description available' }}
        </div>

        <!-- Action button -->
        <div
          class="flex justify-end gap-4 opacity-0 group-hover:opacity-100 transition-opacity row-span-2 items-center"
        >
          <woot-button
            @click.stop="handleEdit(column.name, column)"
            icon="edit"
            size="small"
            class="hover:bg-woot-600 dark:hover:bg-woot-500"
            v-tooltip.top="'Edit description'"
          />
          <woot-button
            @click.stop="handleAllowedValues(column.name, column)"
            icon="add"
            size="small"
            class="hover:bg-woot-600 dark:hover:bg-woot-500"
            v-tooltip.top="'Edit allowed values'"
          />
        </div>

        <!-- Allowed values (row 2, under description) -->
        <div class="flex flex-wrap gap-2 text-xs text-slate-500 dark:text-slate-400">
          <template v-if="column.allowed_values && column.allowed_values.length">
            <span
              v-for="(val, idx) in column.allowed_values"
              :key="idx"
              class="px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-600 text-slate-700 dark:text-slate-200"
            >
              {{ val }}
            </span>
          </template>
          <span v-else class="italic text-red-500 dark:text-red-400">
            No allowed values provided.
          </span>
        </div>
      </div>

      <!-- Pagination controls -->
      <div class="flex justify-between items-center mt-4">
        <!-- Page Size -->
        <div class="flex items-center">
          <label for="pageSize" class="text-sm text-slate-600 dark:text-slate-300 mr-3">Rows per page:</label>
          <select
            id="pageSize"
            class="block mb-0 w-20 rounded-md border-slate-300 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 py-2 pl-3 pr-8 text-sm focus:border-slate-500 focus:ring-slate-500 focus:outline-none"
            v-model="pageSize"
            @change="handlePageSizeChange"
          >
            <option v-for="size in [10,20,50,100]" :key="size" :value="size">
              {{ size }}
            </option>
          </select>
        </div>

        <!-- Pagination -->
        <div class="flex items-center space-x-4">
          <woot-button 
            icon="chevron-left"
            color-scheme="secondary"
            variant="clear"
            class="hover:bg-slate-50 dark:hover:bg-slate-700"
            :disabled="currentPage === 1"
            @click="changePage(currentPage - 1)"
          />
          <span class="text-sm text-slate-600 dark:text-slate-300">
            Page <span class="font-medium text-slate-900 dark:text-slate-100">{{ currentPage }}</span> of
            <span class="font-medium text-slate-900 dark:text-slate-100">{{ totalPages }}</span>
          </span>
          <woot-button 
            icon="chevron-right"
            color-scheme="secondary"
            variant="clear"
            class="hover:bg-slate-50 dark:hover:bg-slate-700"
            :disabled="currentPage === totalPages"
            @click="changePage(currentPage + 1)"
          />
        </div>
      </div>
    </div>
  </div>
</template>
